import React from 'react';
import AppBar from '@material-ui/core/AppBar';
import Divider from '@material-ui/core/Divider';
import Drawer from '@material-ui/core/Drawer';
import Hidden from '@material-ui/core/Hidden';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import MenuIcon from '@material-ui/icons/Menu';
import Toolbar from '@material-ui/core/Toolbar';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Dashboard from '@material-ui/icons/Dashboard';
import People from '@material-ui/icons/PeopleAlt';
import Incidence from '@material-ui/icons/Error';
import HomeWorkIcon from '@material-ui/icons/HomeWork';
import CallIcon from '@material-ui/icons/Call';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import EventNoteIcon from '@material-ui/icons/EventNote';
import { Link, withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../redux/ducks/auth';
import { isAdmin } from '../../services/auth/auth-service';
import PersonIcon from '@material-ui/icons/Person';
import ProgressBar from '../../UI/ProgressBar/progress-bar';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';

const drawerWidth = 260;

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
  },
  drawer: {
    [theme.breakpoints.up('md')]: {
      width: drawerWidth,
      flexShrink: 0,
    },
  },
  appBar: {
    [theme.breakpoints.up('md')]: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: drawerWidth,
    },
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
  },
  menuButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  toolbar: theme.mixins.toolbar,
  drawerPaper: {
    width: drawerWidth,
    backgroundColor: theme.palette.background.paper,
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(3),
    },
    maxWidth: '100%',
    overflow: 'hidden',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
    gap: theme.spacing(1),
  },
  listItem: {
    borderRadius: '0 24px 24px 0',
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  listItemActive: {
    backgroundColor: `${theme.palette.primary.main}15`,
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: `${theme.palette.primary.main}25`,
    },
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    ...theme.mixins.toolbar,
  },
  logo: {
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  navIcon: {
    minWidth: 40,
  },
  activeIcon: {
    color: theme.palette.primary.main,
  },
}));

function DashboardNavbar({ container, history, children, location }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const loading = useSelector(state => state.request.loading);
  const auth = useSelector(state => state.auth);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const dispatch = useDispatch();

  const logoutUser = () => {
    dispatch(logout());
    history.push('/');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavClick = (path) => {
    history.push(path);
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };

  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  const menuItems = [
    { path: '/dashboard/main', icon: <Dashboard />, label: t('dashboard.drawer.dashboard'), adminOnly: false },
    { path: '/dashboard/users', icon: <SupervisorAccountIcon />, label: t('dashboard.drawer.users'), adminOnly: true },
    { path: '/dashboard/events', icon: <EventNoteIcon />, label: t('dashboard.drawer.events'), adminOnly: true },
    { path: '/dashboard/customers', icon: <People />, label: t('dashboard.drawer.customers'), adminOnly: false },
    { path: '/dashboard/incidences', icon: <Incidence />, label: t('dashboard.drawer.incidences'), adminOnly: false },
    { path: '/dashboard/assistance', icon: <HomeWorkIcon />, label: t('dashboard.drawer.assistance'), adminOnly: false },
    { path: '/dashboard/orders', icon: <ShoppingCartIcon />, label: t('dashboard.drawer.orders'), adminOnly: false },
    { path: '/dashboard/messages', icon: <CallIcon />, label: t('dashboard.drawer.messages'), adminOnly: false },
  ];

  const drawer = (
    <div>
      <div className={classes.drawerHeader}>
        <Typography variant="h5" className={classes.logo}>
          {t('app_name')}
        </Typography>
      </div>
      <Divider />
      <List>
        {menuItems.map((item) => {
          if (item.adminOnly && !isAdmin(auth.role)) return null;
          const active = isActive(item.path);
          return (
            <ListItem
              button
              key={item.path}
              onClick={() => handleNavClick(item.path)}
              className={`${classes.listItem} ${active ? classes.listItemActive : ''}`}
            >
              <ListItemIcon className={`${classes.navIcon} ${active ? classes.activeIcon : ''}`}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItem>
          );
        })}
      </List>
      <Divider />
      <List>
        <ListItem
          button
          onClick={() => handleNavClick('/dashboard/profile')}
          className={`${classes.listItem} ${isActive('/dashboard/profile') ? classes.listItemActive : ''}`}
        >
          <ListItemIcon className={`${classes.navIcon} ${isActive('/dashboard/profile') ? classes.activeIcon : ''}`}>
            <PersonIcon />
          </ListItemIcon>
          <ListItemText primary={t('dashboard.drawer.profile')} />
        </ListItem>
        <ListItem
          button
          onClick={logoutUser}
          className={classes.listItem}
        >
          <ListItemIcon className={classes.navIcon}>
            <ExitToAppIcon />
          </ListItemIcon>
          <ListItemText primary={t('dashboard.drawer.logout')} />
        </ListItem>
      </List>
    </div>
  );

  return (
    <div className={classes.root}>
      <AppBar position="fixed" className={classes.appBar} elevation={1}>
        {loading ? <ProgressBar /> : null}
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            className={classes.menuButton}
          >
            <MenuIcon />
          </IconButton>
          <Box className={classes.userInfo}>
            <PersonIcon color="primary" />
            <Typography variant="subtitle1" noWrap>
              {auth.name}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      <nav className={classes.drawer} aria-label="mailbox folders">
        <Hidden mdUp implementation="css">
          <Drawer
            container={container}
            variant="temporary"
            anchor={theme.direction === 'rtl' ? 'right' : 'left'}
            open={mobileOpen}
            onClose={handleDrawerToggle}
            classes={{
              paper: classes.drawerPaper,
            }}
            ModalProps={{
              keepMounted: true,
            }}
          >
            {drawer}
          </Drawer>
        </Hidden>
        <Hidden smDown implementation="css">
          <Drawer
            classes={{
              paper: classes.drawerPaper,
            }}
            variant="permanent"
            open
          >
            {drawer}
          </Drawer>
        </Hidden>
      </nav>
      <main className={classes.content}>
        <div className={classes.toolbar} />
        {children}
      </main>
    </div>
  );
}

export default withRouter(DashboardNavbar);
